import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Autocomplete, Dialog, TextField } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import {
  LuChevronLeft,
  LuChevronRight,
  LuChevronsUpDown,
  LuEllipsis,
  LuEye,
  LuFileText,
  LuGitMerge,
  LuMenu,
  LuPencil,
  LuPlus,
  LuSearch,
  LuTrash2,
  LuTriangleAlert,
  LuX,
} from "react-icons/lu";

import { useMaterialUIController } from "context";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import AppHeader from "components/AppHeader";
import ListingActionMenu from "components/ListingActionMenu";
import ThemedSelect from "components/FormControls/ThemedSelect";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import PickDate from "components/DatePicker/DatePicker";
import { TableSkeleton } from "components/Skeleton";
import { loadingAction } from "ReduxReducers/loadingReducer";
import { appConstants } from "utils/appConstants";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { createStatementAttachment } from "utils/statementPdf";
import InvoiceAttachmentPreview from "layouts/Invoices/InvoiceAttachmentPreview";
import { getScreenRight, showResMsg } from "utils/generic";
import { genericMessage } from "utils/genericMessages";

import {
  MergeContactTransaction,
  deleteContactRecords,
  getAllContactsList,
  getBusinessUserContacts,
  getCustomerActivityStatements,
} from "utils/contactAPI";

import ContactEmptyState from "./ContactEmptyState";

import "./contacts.css";
import "layouts/Quotation/quotation.css";

const SCREEN_NAME = "Contacts";
const CONTACT_FILTERS = [
  { key: "contactStatus", label: "Contact status", options: [["All", "All"], ["Active", "Active"], ["Archived", "Archived"]] },
  { key: "balance", label: "Outstanding balance", options: [["Any", "All"], ["Has", "Has outstanding balance"], ["No", "No outstanding balance"]] },
  { key: "terms", label: "Payment terms", options: [["Any", "All"], ["7 days", "7 days"], ["14 days", "14 days"], ["30 days", "30 days"], ["custom", "Custom range"]] },
  { key: "reminders", label: "Payment reminders", options: [["Any", "All"], ["Reminders on", "Reminders on"], ["Reminders off", "Reminders off"]] },
];

const getInitials = (str = "") => {
  if (!str) return "CO";
  const clean = String(str)
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "CO";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const formatABN = (abn = "") => {
  if (!abn) return "—";
  const digits = String(abn).replace(/\s+/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(
      5,
      8
    )} ${digits.slice(8, 11)}`;
  }
  return String(abn);
};

const formatTerms = (terms) => {
  if (!terms) return "14 days";
  const t = String(terms).trim().toLowerCase();
  if (t.includes("day")) return t;
  return `${terms} days`;
};

const isReminderEnabled = (row) => {
  if (
    row.invoiceReminder === "true" ||
    row.invoiceReminder === true ||
    row.invoiceReminder === "On"
  )
    return true;
  if (
    row.autoInvoiceReminderFlag === true ||
    row.autoInvoiceReminderFlag === "true"
  )
    return true;
  return false;
};

export default function Contacts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Authentication & Screen rights
  const [loginDetails] = useState(() =>
    JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}")
  );
  const [screenAccess, setScreenAccess] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  // Profile data for AppHeader & mobile branding
  const { organisation, role, userName, profileImage, planName } =
    useMemo(() => {
      const org =
        loginDetails.companyName ||
        loginDetails.businessName ||
        loginDetails.organisationName ||
        loginDetails.fullName ||
        loginDetails.name ||
        "Invoice & Recover";
      const r = loginDetails.roleName || loginDetails.userType || "Owner";
      const user =
        loginDetails.fullName ||
        loginDetails.name ||
        `${loginDetails.firstName || ""} ${
          loginDetails.lastName || ""
        }`.trim() ||
        org;
      const profileFileName =
        loginDetails.profilePictureFileName || loginDetails.profilePicFileName;
      const img = profileFileName
        ? `${appConstants.baseURL}user/viewFile?fileName=${encodeURIComponent(
            profileFileName
          )}`
        : "";
      const plan =
        loginDetails.planName || loginDetails.subscriptionPlan || "SILVER";
      return {
        organisation: org,
        role: r,
        userName: user,
        profileImage: img,
        planName: plan,
      };
    }, [loginDetails]);

  // Data & Table state
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [contactTypeCounts, setContactTypeCounts] = useState({
    all: 0,
    customers: 0,
    suppliers: 0,
  });
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [countsLoading, setCountsLoading] = useState(true);
  const paginationRequest = useRef(false);
  const requestSequence = useRef(0);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(() => {
    const urlType = new URLSearchParams(window.location.search).get("ctype");
    if (urlType === "Customer") return "Customer";
    if (urlType === "Supplier") return "Supplier";
    return "All";
  });

  // Register dropdown filters
  const [appliedDrawerFilters, setAppliedDrawerFilters] = useState({
    contactStatus: "All",
    balance: "Any",
    terms: "Any",
    reminders: "Any",
  });
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [rangeDraft, setRangeDraft] = useState({ from: "", to: "" });
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeError, setRangeError] = useState("");
  const customRangeApplied = appliedDrawerFilters.terms === "custom";
  const dateFilter = useMemo(() => customRangeApplied
    ? { durationDate: "custom", fromDate: dateRange.from, toDate: dateRange.to }
    : {}, [customRangeApplied, dateRange.from, dateRange.to]);

  // Action Menu state
  const [openMenuRowId, setOpenMenuRowId] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  // Modals state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [sourceMergeContact, setSourceMergeContact] = useState(null);
  const [availableMergeContacts, setAvailableMergeContacts] = useState([]);
  const [selectedMergeTarget, setSelectedMergeTarget] = useState(null);

  const [statementModalOpen, setStatementModalOpen] = useState(false);
  const [statementAction, setStatementAction] = useState("");
  const [statementPreview, setStatementPreview] = useState(null);
  const [statementTargetContactIds, setStatementTargetContactIds] = useState(
    []
  );
  const [statementDuration, setStatementDuration] = useState("lastMonth");
  const [statementFromDate, setStatementFromDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth() - 1, 1);
  });
  const [statementToDate, setStatementToDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 0);
  });

  // Check screen rights
  useEffect(() => {
    getScreenRight(SCREEN_NAME).then((right) => {
      setScreenAccess(right === "Full Access");
    });
  }, []);

  // Close row action menu on click outside
  // Fetch contacts from backend
  const fetchContacts = useCallback(async () => {
    const request = ++requestSequence.current;
    setLoading(!paginationRequest.current);
    paginationRequest.current = false;
    const payload = {
      ...dateFilter,
      businessUserID: loginDetails?.businessUserID,
      token: sessionStorage.getItem("TOKEN"),
      contactType: activeTab === "All" ? "All" : activeTab,
      contactStatus:
        appliedDrawerFilters.contactStatus === "All"
          ? "All"
          : appliedDrawerFilters.contactStatus,
      filterValues: search.trim(),
      searchParameterList: [
        "name",
        "streetAddress",
        "emailAddress",
        "finalAmount",
        "youOwe",
        "theyOwe",
        "contactType",
        "companyName",
        "abnNumber",
      ],
      page,
      limit: perPage,
      perPage,
    };

    getBusinessUserContacts(
      payload,
      (data) => {
        if (request !== requestSequence.current) return;
        setLoading(false);
        if (data?.Result_Output?.docs) {
          setRows(data.Result_Output.docs);
          setTotalRows(data.Result_Output.totalDocs || 0);
        } else {
          setRows([]);
          setTotalRows(0);
        }
      },
      dispatch
    );
  }, [
    activeTab,
    appliedDrawerFilters.contactStatus,
    dateFilter,
    dispatch,
    loginDetails?.businessUserID,
    page,
    perPage,
    search,
  ]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    if (!loginDetails?.businessUserID) return undefined;
    const request = new AbortController();
    setCountsLoading(true);
    const basePayload = {
      ...dateFilter,
      businessUserID: loginDetails.businessUserID,
      token: sessionStorage.getItem("TOKEN"),
      contactStatus: appliedDrawerFilters.contactStatus,
      filterValues: search.trim(),
      searchParameterList: [
        "name",
        "streetAddress",
        "emailAddress",
        "finalAmount",
        "youOwe",
        "theyOwe",
        "contactType",
        "companyName",
        "abnNumber",
      ],
      page: 1,
      limit: 1,
      perPage: 1,
    };
    const headers = {
      Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
    };
    const countFor = (contactType) =>
      axios
        .post(
          `${appConstants.baseURL}contact/fetchContacts`,
          { ...basePayload, contactType },
          { headers, signal: request.signal }
        )
        .then(({ data }) => Number(data?.Result_Output?.totalDocs || 0));

    Promise.all([countFor("All"), countFor("Customer"), countFor("Supplier")])
      .then(([all, customers, suppliers]) =>
        setContactTypeCounts({ all, customers, suppliers })
      )
      .catch((error) => {
        if (error.code !== "ERR_CANCELED")
          console.error("Unable to load contact counts", error);
      })
      .finally(() => {
        if (!request.signal.aborted) setCountsLoading(false);
      });
    return () => request.abort();
  }, [
    appliedDrawerFilters.contactStatus,
    dateFilter,
    loginDetails?.businessUserID,
    search,
  ]);

  // Client-side filtering for balance, terms and reminders
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      // Balance filter
      if (appliedDrawerFilters.balance === "Has") {
        const bal = Number(row.finalAmount || row.theyOwe || 0);
        if (bal <= 0) return false;
      } else if (appliedDrawerFilters.balance === "No") {
        const bal = Number(row.finalAmount || row.theyOwe || 0);
        if (bal > 0) return false;
      }

      // Terms filter
      if (appliedDrawerFilters.terms !== "Any" && appliedDrawerFilters.terms !== "custom") {
        const t = String(row.paymentTerms || "").toLowerCase();
        if (appliedDrawerFilters.terms === "7 days" && !t.includes("7"))
          return false;
        if (appliedDrawerFilters.terms === "14 days" && !t.includes("14"))
          return false;
        if (appliedDrawerFilters.terms === "30 days" && !t.includes("30"))
          return false;
      }

      // Reminders filter
      if (appliedDrawerFilters.reminders === "Reminders on") {
        if (!isReminderEnabled(row)) return false;
      } else if (appliedDrawerFilters.reminders === "Reminders off") {
        if (isReminderEnabled(row)) return false;
      }

      return true;
    });
  }, [rows, appliedDrawerFilters]);

  // Compute counts for tab badges
  const counts = contactTypeCounts;
  const contentLoading = loading || countsLoading;

  // Active dropdown filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (appliedDrawerFilters.contactStatus !== "All") count++;
    if (appliedDrawerFilters.balance !== "Any") count++;
    if (appliedDrawerFilters.terms !== "Any") count++;
    if (appliedDrawerFilters.reminders !== "Any") count++;
    return count;
  }, [appliedDrawerFilters]);

  // Search input handler
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearch("");
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    if (key === "terms" && value === "custom") {
      setRangeDraft(dateRange);
      setRangeError("");
      setRangeOpen(true);
      return;
    }
    setAppliedDrawerFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    const defaults = Object.fromEntries(CONTACT_FILTERS.map(({ key, options }) => [key, options[0][0]]));
    setAppliedDrawerFilters(defaults);
    setDateRange({ from: "", to: "" });
    setSearch("");
    setPage(1);
  };

  // Delete Contact
  const handleDeleteClick = (row) => {
    setOpenMenuRowId(null);
    setContactToDelete(row);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!contactToDelete) return;
    dispatch(loadingAction(true));
    deleteContactRecords(
      { _id: [contactToDelete._id], token: sessionStorage.getItem("TOKEN") },
      (res) => {
        dispatch(loadingAction(false));
        showResMsg(
          res.Result_Status ? genericMessage.deleted : res.Result_Message,
          1500,
          res.Result_Status
        );
        setDeleteModalOpen(false);
        setContactToDelete(null);
        fetchContacts();
      },
      dispatch
    );
  };

  // Merge Contact
  const handleMergeClick = (row) => {
    setOpenMenuRowId(null);
    setSourceMergeContact(row);
    setSelectedMergeTarget(null);
    getAllContactsList(
      {
        businessUserID: loginDetails?.businessUserID,
        token: sessionStorage.getItem("TOKEN"),
      },
      (data) => {
        const list = data?.Result_Output || [];
        const filtered = list.filter(
          (c) =>
            c.contactType === row.contactType &&
            c._id !== row._id &&
            c.contactStatus === "Active"
        );
        if (filtered.length > 0) {
          setAvailableMergeContacts(filtered);
          setMergeModalOpen(true);
        } else {
          showResMsg(
            "No other active contacts of this type available to merge with.",
            2000,
            false
          );
        }
      },
      dispatch
    );
  };

  const handleConfirmMerge = () => {
    if (!sourceMergeContact || !selectedMergeTarget) return;
    const payload = {
      businessUserID: loginDetails?.businessUserID,
      contactType: sourceMergeContact.contactType,
      merge_ID: sourceMergeContact._id,
      merge_Contact: sourceMergeContact.name || sourceMergeContact.companyName,
      mergeWith_ID: selectedMergeTarget._id,
      mergeWith_ContactName:
        selectedMergeTarget.name || selectedMergeTarget.companyName,
    };

    MergeContactTransaction(
      payload,
      (data) => {
        showResMsg(data.Result_Message, 2000, data.Result_Status);
        setMergeModalOpen(false);
        setSourceMergeContact(null);
        setSelectedMergeTarget(null);
        fetchContacts();
      },
      dispatch
    );
  };

  // Statements
  const handleOpenStatementModal = (ids) => {
    setStatementTargetContactIds(ids);
    setStatementModalOpen(true);
  };

  const handleStatementAction = async (type) => {
    if (!statementTargetContactIds.length || statementAction) return;
    let startDate = new Date(statementFromDate);
    let endDate = new Date(statementToDate);

    if (statementDuration === "today") {
      startDate = new Date();
      endDate = new Date();
    } else if (statementDuration === "lastWeek") {
      startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      endDate = new Date();
    } else if (statementDuration === "lastMonth") {
      const d = new Date();
      startDate = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      endDate = new Date(d.getFullYear(), d.getMonth(), 0);
    } else if (statementDuration === "lastYear") {
      const d = new Date();
      startDate = new Date(d.getFullYear() - 1, 0, 1);
      endDate = new Date(d.getFullYear() - 1, 11, 31);
    }

    startDate.setUTCHours(0, 0, 0, 0);
    endDate.setUTCHours(23, 59, 59, 999);

    if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime()) || startDate > endDate) {
      showResMsg("Select a valid statement date range.", 2000, false);
      return;
    }
    setStatementAction(type);
    try {
      await getCustomerActivityStatements(
        {
          businessUserID: loginDetails?.businessUserID,
          contactIDs: statementTargetContactIds,
          from: startDate,
          to: endDate,
          type,
        },
        async (dataRes) => {
          if (dataRes?.Result_Status) {
            if (type === "Print") {
              setStatementPreview(createStatementAttachment(dataRes.Result_Output));
            } else {
              showDraftSavedMessage("Statement email sent successfully");
            }
            setStatementModalOpen(false);
          } else {
            showResMsg(
              dataRes?.Result_Message || "Failed to generate statement.",
              2000,
              false
            );
          }
        },
        dispatch
      );
    } catch (error) {
      showResMsg(error.message || "Unable to send or generate the statement. Please try again.", 2000, false);
    } finally {
      setStatementAction("");
    }
  };

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const handlePageChange = (nextPage) => {
    if (nextPage === page) return;
    paginationRequest.current = true;
    setPage(nextPage);
  };
  const startRow = totalRows === 0 ? 0 : (page - 1) * perPage + 1;
  const endRow = Math.min(page * perPage, totalRows);

  return (
    <div
      className={`contacts-page business-dashboard${darkMode ? " dark" : ""}`}
      data-theme={darkMode ? "dark" : "light"}
    >
      {/* SIDEBAR NAVIGATION */}
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        organisation={organisation}
        role={role}
        planName={planName}
      />

      <div className="dashboard-main">
        {/* TOP APP HEADER */}
        <AppHeader
          onOpenNav={() => setNavOpen(true)}
          userName={userName}
          profileImage={profileImage}
        />

        {/* MAIN WORKSPACE CONTENT */}
        <main className="dashboard-content contacts-content">
          {/* TABLET / MOBILE IDENTITY & ORG ROW */}
          <div className="quotation-tablet-identity-row dashboard-org-product-row">
            <div className="dashboard-product-identity">
              <button
                type="button"
                className="dashboard-launcher-btn"
                onClick={() => setNavOpen(true)}
                aria-label="Open navigation"
              >
                <LuMenu />
              </button>
              <span className="dashboard-product-name">
                invoice &amp; recover
              </span>
            </div>
            <button className="dashboard-mobile-org" type="button">
              <span>{getInitials(organisation)}</span>
              <div>
                <strong>{organisation}</strong>
                <small>{role}</small>
              </div>
              <LuChevronsUpDown className="org-switch-icon" />
            </button>
          </div>

          {/* PAGE HEADER (Figma 1529:154312) */}
          <section className="contacts-page-header">
            <div className="contacts-title-row">
              <div className="contacts-page-identity">
                <h1>Contacts</h1>
                <p>Customers and suppliers</p>
              </div>

              <div className="contacts-header-actions">
                <button
                  type="button"
                  className="contacts-import-btn"
                  onClick={() =>
                    showResMsg(
                      "Import feature is available via CSV upload in Settings.",
                      2500,
                      true
                    )
                  }
                >
                  Import
                </button>

                {screenAccess ? (
                  <Link
                    to="/contacts/addContact"
                    className="contacts-create-btn"
                  >
                    <LuPlus />
                    <span>Add Contact</span>
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="contacts-create-btn"
                    style={{ opacity: 0.6, cursor: "not-allowed" }}
                    disabled
                  >
                    <LuPlus />
                    <span>Add Contact</span>
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* CONTACTS MANAGEMENT (Figma 1529:154320) */}
          <section className="contacts-filter-section">
            {/* TABS (Figma 1529:154322) */}
            <div className="contacts-tabs">
              <button
                type="button"
                className={`contacts-tab${
                  activeTab === "All" ? " active" : ""
                }`}
                onClick={() => {
                  setActiveTab("All");
                  setPage(1);
                }}
              >
                All ({counts.all})
              </button>
              <button
                type="button"
                className={`contacts-tab${
                  activeTab === "Customer" ? " active" : ""
                }`}
                onClick={() => {
                  setActiveTab("Customer");
                  setPage(1);
                }}
              >
                Customers ({counts.customers})
              </button>
              <button
                type="button"
                className={`contacts-tab${
                  activeTab === "Supplier" ? " active" : ""
                }`}
                onClick={() => {
                  setActiveTab("Supplier");
                  setPage(1);
                }}
              >
                Suppliers ({counts.suppliers})
              </button>
            </div>

            {/* CONTROLS ROW: SEARCH & FILTERS (Figma 1529:154329) */}
            <div className="contacts-controls-row">
              <div className="contacts-search-box">
                <LuSearch className="contacts-search-icon" />
                <input
                  type="text"
                  className="contacts-search-input"
                  placeholder="Search name, email or ABN"
                  value={search}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") fetchContacts();
                  }}
                />
                {search && (
                  <button
                    type="button"
                    className="contacts-search-clear"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    <LuX />
                  </button>
                )}
              </div>

              {CONTACT_FILTERS.map(({ key, label, options }) => (
                <ThemedSelect
                  key={key}
                  className="contacts-register-filter"
                  aria-label={label}
                  dark={darkMode}
                  value={appliedDrawerFilters[key]}
                  menuPortalContainerSelector=".contacts-page"
                  displayValue={appliedDrawerFilters[key] === options[0][0] ? label
                    : key === "terms" && appliedDrawerFilters.terms === "custom" && dateRange.from && dateRange.to
                      ? `${format(parseISO(dateRange.from), "dd MMM yyyy")} – ${format(parseISO(dateRange.to), "dd MMM yyyy")}`
                      : undefined}
                  onChange={(event) => handleFilterChange(key, event.target.value)}
                >
                  {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
                </ThemedSelect>
              ))}
              {(activeFiltersCount > 0 || search.trim()) && (
                <button type="button" className="quotation-clear-filters-btn contacts-clear-filters" onClick={handleClearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          </section>

          {/* CONTACTS TABLE CARD (Figma 1529:154332) */}
          <div
            className={`contacts-table-card${
              !contentLoading && filteredRows.length === 0 ? " is-empty" : ""
            }`}
          >
            {/* Register Context Header */}
            {(contentLoading || filteredRows.length > 0) && (
              <div className="contacts-table-context">
                <span className="contacts-table-count">
                  {totalRows} contact{totalRows !== 1 ? "s" : ""}
                </span>
                <span className="contacts-table-scope">
                  {activeTab === "All" ? "All contacts" : `${activeTab}s`}
                </span>
              </div>
            )}

            {/* Table Content */}
            {!contentLoading && filteredRows.length === 0 ? (
              <ContactEmptyState
                canCreate={screenAccess}
                variant={
                  search.trim() !== "" ||
                  activeFiltersCount > 0 ||
                  activeTab !== "All"
                    ? "noResults"
                    : "empty"
                }
              />
            ) : (
              <div className="contacts-table-wrapper">
                <table className="contacts-table" aria-busy={contentLoading}>
                  <thead className="contacts-thead">
                    <tr>
                      <th className="contacts-th th-name">Name</th>
                      <th className="contacts-th th-contact">Contact</th>
                      <th className="contacts-th th-abn">ABN</th>
                      <th className="contacts-th th-terms">Terms</th>
                      <th className="contacts-th th-reminders">Reminders</th>
                      <th className="contacts-th th-balance">Open balance</th>
                      <th className="contacts-th th-actions">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="contacts-tbody">
                    {contentLoading ? (
                      <TableSkeleton
                        variant="rows"
                        columns={6}
                        rows={perPage}
                        hasActions
                      />
                    ) : (
                      filteredRows.map((row) => {
                        const isMenuOpen = openMenuRowId === row._id;
                        const displayName =
                          row.companyName || row.name || "Unnamed contact";
                        const contactPerson = row.name || displayName;
                        const reminderOn = isReminderEnabled(row);
                        const balanceAmount = Number(
                          row.finalAmount || row.theyOwe || 0
                        );

                        return (
                          <tr
                            key={row._id}
                            className="contacts-clickable-row"
                            role="link"
                            tabIndex={0}
                            aria-label={`View ${displayName} details`}
                            onClick={(event) => {
                              if (
                                event.target.closest(
                                  "a, button, input, select, textarea"
                                )
                              ) {
                                return;
                              }
                              navigate(`/contacts/${row._id}`);
                            }}
                            onKeyDown={(event) => {
                              if (
                                event.target === event.currentTarget &&
                                event.key === "Enter"
                              ) {
                                navigate(`/contacts/${row._id}`);
                              }
                            }}
                          >
                            {/* Name + Avatar + Type */}
                            <td className="contacts-td">
                              <div className="contacts-name-cell">
                                <div className="contacts-avatar">
                                  {getInitials(displayName)}
                                </div>
                                <div className="contacts-name-info">
                                  <Link
                                    to={`/contacts/${row._id}`}
                                    className="contacts-company-link"
                                  >
                                    <span
                                      className="contacts-company-name"
                                      title={displayName}
                                    >
                                      {displayName}
                                    </span>
                                  </Link>
                                  <span className="contacts-type-sub">
                                    {row.contactType || "Customer"}
                                  </span>
                                </div>
                              </div>
                            </td>

                            {/* Contact Person + Email */}
                            <td className="contacts-td">
                              <div className="contacts-contact-cell">
                                <span
                                  className="contacts-contact-name"
                                  title={contactPerson}
                                >
                                  {contactPerson}
                                </span>
                                <span
                                  className="contacts-contact-email"
                                  title={row.emailAddress || ""}
                                >
                                  {row.emailAddress || "—"}
                                </span>
                              </div>
                            </td>

                            {/* ABN */}
                            <td className="contacts-td">
                              <span className="contacts-abn-text">
                                {formatABN(row.abnNumber)}
                              </span>
                            </td>

                            {/* Terms */}
                            <td className="contacts-td">
                              <span className="contacts-terms-text">
                                {formatTerms(row.paymentTerms)}
                              </span>
                            </td>

                            {/* Reminders Pill */}
                            <td className="contacts-td">
                              <span
                                className={`contacts-reminder-pill ${
                                  reminderOn ? "pill-on" : "pill-off"
                                }`}
                              >
                                {reminderOn ? "On" : "Off"}
                              </span>
                            </td>

                            {/* Open Balance */}
                            <td className="contacts-td">
                              <div className="contacts-balance-cell">
                                {formatCurrencyAUD(balanceAmount)}
                              </div>
                            </td>

                            {/* 3-dots Actions Menu */}
                            <td className="contacts-td contacts-actions-cell">
                              <button
                                type="button"
                                className="contacts-action-trigger listing-action-trigger"
                                onClick={(event) => {
                                  const isOpen = openMenuRowId === row._id;
                                  setOpenMenuRowId(isOpen ? null : row._id);
                                  setMenuAnchorEl(
                                    isOpen ? null : event.currentTarget
                                  );
                                }}
                                aria-label="Row actions"
                                aria-expanded={isMenuOpen}
                              >
                                <LuEllipsis />
                              </button>

                              {isMenuOpen && (
                                <ListingActionMenu
                                  anchorEl={menuAnchorEl}
                                  label={`Actions for ${displayName}`}
                                  onClose={() => {
                                    setOpenMenuRowId(null);
                                    setMenuAnchorEl(null);
                                  }}
                                >
                                  <Link
                                    to={`/contacts/${row._id}`}
                                    className="contacts-menu-item"
                                    onClick={() => setOpenMenuRowId(null)}
                                  >
                                    <LuEye />
                                    <span>View details</span>
                                  </Link>

                                  <Link
                                    to={`/contacts/editContact/${row._id}`}
                                    className="contacts-menu-item"
                                    onClick={() => setOpenMenuRowId(null)}
                                  >
                                    <LuPencil />
                                    <span>Edit</span>
                                  </Link>

                                  <button
                                    type="button"
                                    className="contacts-menu-item"
                                    onClick={() => handleMergeClick(row)}
                                  >
                                    <LuGitMerge />
                                    <span>Merge</span>
                                  </button>

                                  <button
                                    type="button"
                                    className="contacts-menu-item"
                                    onClick={() => {
                                      setOpenMenuRowId(null);
                                      handleOpenStatementModal([row._id]);
                                    }}
                                  >
                                    <LuFileText />
                                    <span>Statement</span>
                                  </button>

                                  <button
                                    type="button"
                                    className="contacts-menu-item delete"
                                    onClick={() => handleDeleteClick(row)}
                                  >
                                    <LuTrash2 />
                                    <span>Delete</span>
                                  </button>
                                </ListingActionMenu>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Footer (Figma 1529:154479) */}
            {(contentLoading || filteredRows.length > 0) && (
              <div className={`contacts-pagination${contentLoading ? " is-loading" : ""}`}>
                <span className="contacts-pagination-info">
                  Showing {startRow}–{endRow} of {totalRows} contacts
                </span>

                <div className="contacts-pagination-nav">
                  <button
                    type="button"
                    className="contacts-page-btn"
                    disabled={contentLoading || page <= 1}
                    onClick={() => handlePageChange(Math.max(1, page - 1))}
                    aria-label="Previous page"
                  >
                    <LuChevronLeft />
                  </button>
                  <button
                    type="button"
                    className="contacts-page-btn"
                    disabled={contentLoading || page >= totalPages}
                    onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                    aria-label="Next page"
                  >
                    <LuChevronRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* DELETE CONFIRMATION MODAL (Figma 1591:150817) */}
      {deleteModalOpen && contactToDelete && (
        <div
          className="contacts-modal-scrim"
          onClick={() => setDeleteModalOpen(false)}
        >
          <div
            className="contacts-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="contacts-modal-header">
              <div className="contacts-modal-title-wrap">
                <div className="contacts-modal-warn-icon">
                  <LuTriangleAlert />
                </div>
                <h3 className="contacts-modal-title">Delete contact</h3>
              </div>
              <button
                type="button"
                className="contacts-modal-close"
                onClick={() => setDeleteModalOpen(false)}
                aria-label="Close dialog"
              >
                <LuX />
              </button>
            </div>

            <p className="contacts-modal-body">
              Delete{" "}
              {contactToDelete.companyName ||
                contactToDelete.name ||
                "this contact"}{" "}
              permanently? Financial records remain available. This action
              can&apos;t be undone.
            </p>

            <div className="contacts-modal-actions">
              <button
                type="button"
                className="contacts-modal-cancel"
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="contacts-modal-danger"
                onClick={handleConfirmDelete}
              >
                Delete contact
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MERGE CONTACT MODAL */}
      {mergeModalOpen && sourceMergeContact && (
        <div
          className="contacts-modal-scrim"
          onClick={() => setMergeModalOpen(false)}
        >
          <div
            className="contacts-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="contacts-modal-header">
              <div className="contacts-modal-title-wrap">
                <div
                  className="contacts-modal-warn-icon"
                  style={{
                    background: "var(--interactive-soft)",
                    color: "var(--interactive-default)",
                  }}
                >
                  <LuGitMerge />
                </div>
                <h3 className="contacts-modal-title">
                  Merge transaction history
                </h3>
              </div>
              <button
                type="button"
                className="contacts-modal-close"
                onClick={() => setMergeModalOpen(false)}
                aria-label="Close dialog"
              >
                <LuX />
              </button>
            </div>

            <p className="contacts-modal-body">
              <strong>
                {sourceMergeContact.name || sourceMergeContact.companyName}
              </strong>{" "}
              ({sourceMergeContact.contactType}) will be{" "}
              <span style={{ color: "var(--danger-text)", fontWeight: 600 }}>
                deleted
              </span>{" "}
              and their transaction history will be merged with the contact
              selected below:
            </p>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "var(--dash-muted)",
                  marginBottom: "6px",
                }}
              >
                Contact to merge with ({sourceMergeContact.contactType})
              </label>
              <Autocomplete
                options={availableMergeContacts}
                getOptionLabel={(option) =>
                  option.name || option.companyName || ""
                }
                value={selectedMergeTarget}
                onChange={(_, val) => setSelectedMergeTarget(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search for a contact"
                    size="small"
                    variant="outlined"
                  />
                )}
              />
            </div>

            <div className="contacts-modal-actions">
              <button
                type="button"
                className="contacts-modal-cancel"
                onClick={() => setMergeModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="contacts-apply-btn"
                style={{ flex: "none", padding: "0 20px" }}
                disabled={!selectedMergeTarget}
                onClick={handleConfirmMerge}
              >
                Merge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ACTIVITY STATEMENT MODAL */}
      <InvoiceAttachmentPreview
        attachment={statementPreview}
        onClose={() => setStatementPreview(null)}
        quotationStyle
      />
      <Dialog
        open={rangeOpen}
        onClose={() => setRangeOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: "contacts-date-range-dialog", "data-theme": darkMode ? "dark" : "light" }}
        aria-labelledby="contacts-date-range-title"
        aria-describedby="contacts-date-range-description"
      >
        <h2 id="contacts-date-range-title">Contact date range</h2>
        <p id="contacts-date-range-description">Show contacts created within these dates.</p>
        {["from", "to"].map((field) => (
          <PickDate
            key={field}
            id={`contacts-range-${field}`}
            label={field === "from" ? "From" : "To"}
            selected={rangeDraft[field] ? parseISO(rangeDraft[field]) : null}
            minDate={field === "to" && rangeDraft.from ? parseISO(rangeDraft.from) : undefined}
            maxDate={field === "from" && rangeDraft.to ? parseISO(rangeDraft.to) : undefined}
            dateFormat="dd MMM yyyy"
            onChange={(date) => {
              setRangeDraft((current) => ({ ...current, [field]: date && isValid(date) ? format(date, "yyyy-MM-dd") : "" }));
              setRangeError("");
            }}
          />
        ))}
        {rangeError && <p role="alert">{rangeError}</p>}
        <footer>
          <button type="button" onClick={() => setRangeOpen(false)}>Cancel</button>
          <button type="button" className="contacts-date-range-apply" onClick={() => {
            if (!isValid(parseISO(rangeDraft.from)) || !isValid(parseISO(rangeDraft.to)) || rangeDraft.from > rangeDraft.to) {
              setRangeError("Choose valid start and end dates in order.");
              return;
            }
            setDateRange(rangeDraft);
            setAppliedDrawerFilters((current) => ({ ...current, terms: "custom" }));
            setPage(1);
            setRangeOpen(false);
          }}>Apply date range</button>
        </footer>
      </Dialog>

      {statementModalOpen && (
        <div
          className="contacts-modal-scrim"
          onClick={() => !statementAction && setStatementModalOpen(false)}
        >
          <div
            className="contacts-modal-card"
            role="dialog"
            aria-modal="true"
            aria-label="Activity Statement"
            aria-busy={Boolean(statementAction)}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="contacts-modal-header">
              <div className="contacts-modal-title-wrap">
                <div
                  className="contacts-modal-warn-icon"
                  style={{
                    background: "var(--interactive-soft)",
                    color: "var(--interactive-default)",
                  }}
                >
                  <LuFileText />
                </div>
                <h3 className="contacts-modal-title">Activity Statement</h3>
              </div>
              <button
                type="button"
                className="contacts-modal-close"
                disabled={Boolean(statementAction)}
                onClick={() => setStatementModalOpen(false)}
                aria-label="Close dialog"
              >
                <LuX />
              </button>
            </div>

            <p className="contacts-modal-body">
              Select duration to view or email statements for{" "}
              {statementTargetContactIds.length} contact
              {statementTargetContactIds.length !== 1 ? "s" : ""}.
            </p>

            <div style={{ marginBottom: "16px" }}>
              <label
                htmlFor="contacts-statement-duration"
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "var(--dash-muted)",
                  marginBottom: "6px",
                }}
              >
                Duration
              </label>
              <ThemedSelect
                id="contacts-statement-duration"
                disabled={Boolean(statementAction)}
                aria-label="Duration"
                dark={darkMode}
                menuPortalContainerSelector=".contacts-modal-scrim"
                value={statementDuration}
                onChange={(e) => setStatementDuration(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="lastWeek">Last 7 Days</option>
                <option value="lastMonth">Last Month</option>
                <option value="lastYear">Last 12 Months</option>
                <option value="custom">Custom Date Range</option>
              </ThemedSelect>
            </div>

            {statementDuration === "custom" && (
              <div className="contacts-statement-date-range">
                <div>
                  <PickDate
                    disabled={Boolean(statementAction)}
                    selected={statementFromDate}
                    onChange={(date) => setStatementFromDate(new Date(date))}
                    label="From"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    maxDate={new Date()}
                  />
                </div>
                <div>
                  <PickDate
                    disabled={Boolean(statementAction)}
                    selected={statementToDate}
                    popperPlacement="bottom-end"
                    onChange={(date) => setStatementToDate(new Date(date))}
                    label="To"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    maxDate={new Date()}
                  />
                </div>
              </div>
            )}

            <div className="contacts-modal-actions">
              <button
                type="button"
                className="contacts-modal-cancel"
                disabled={Boolean(statementAction)}
                onClick={() => setStatementModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="contacts-modal-cancel"
                onClick={() => handleStatementAction("Email")}
                disabled={Boolean(statementAction)}
              >
                {statementAction === "Email" ? "Sending…" : "Email"}
              </button>
              <button
                type="button"
                className="contacts-apply-btn"
                style={{ flex: "none", padding: "0 20px" }}
                onClick={() => handleStatementAction("Print")}
                disabled={Boolean(statementAction)}
              >
                {statementAction === "Print" ? "Generating…" : "View PDF"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
